import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import querystring from 'query-string';
import { Redirect, Route, useHistory } from 'react-router-dom';
import { makeStyles } from "@material-ui/core/styles";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { Box } from '@material-ui/core';

// Local
import PanelContainer from '../../components/Panels/PanelContainer';
import Panel from '../../components/Panels/Panel';
import ExerciseDetailsPanel from '../../components/Panels/ExerciseDetailsPanel';
import { updateQuery } from '../../util/utilFunctions';
import { PageHeader, PageToolBar } from '../../components';
import {FloatingCircleButton} from '../../components/Buttons';
import { colors } from '../../styles';
import AddSuperSetsPanel from '../../components/Panels/AddSuperSetsPanel';
import AddCircuitsPanel from '../../components/Panels/AddCircuitsPanel';
import SlidingFlexTransition from '../../components/Transitions/SlidingFlexTransition';
import NoticeBar from '../../components/Notice/NoticeBar';
import { textStrings } from '../../textStrings';
import SupersetCircuitScreen from '../../components/SupersetCircuits/SupersetCircuitScreen';
import CustomBreadcrumb from '../../components/BreadCrumbs/BreadCrumbs';

const views = {
  myExercises: {
    key: 'my-exercises',
    exerciseList: true,
  },
  exerciseList: {
    key: 'exerciseList',
    exerciseList: true,
  },
  exerciseDetails: {
    key: 'exerciseDetails',
    exerciseList: true,
    exerciseDetails: true,
  },
  customExerciseDetails: {
    key: 'customExerciseDetails',
    exerciseList: true,
    customExerciseDetails: true,
  },
  superSetDetails: {
    key: 'superSetDetails',
    exerciseList: true,
    superSetDetails: true,
  },
  circuitDetails: {
    key: 'circuitDetails',
    exerciseList: true,
    circuitDetails: true,
  },
  superSetExerciseDetails: {
    key: 'superSetExerciseDetails',
    superSetDetails: true,
    exerciseDetails: true,
    exerciseList: false,
  },
  circuitExerciseDetails: {
    key: 'circuitExerciseDetails',
    circuitDetails: true,
    exerciseDetails: true,
    exerciseList: false,
  },
};

const breadcrumbItems = [
    { label: textStrings.libraries, path: "/libraries" },
    { label: textStrings.supersetsCircuits },
];

const SUPERSETS_LINK_TO = {
  pathname: "/libraries/sets/supersets",
  state: {
    supersets: true,
  },
};

const CIRCUITS_LINK_TO = {
  pathname: "/libraries/sets/circuits",
  state: {
    supersets: false,
  },
};

const useStyles = makeStyles(() => ({
  createNewTxt: {
    fontSize: "14px",
    padding: "10px",
    paddingLeft: "15px",
  },
  menu: {
    width: 230,
    paddingTop: 0,
    paddingBottom: 0,
  },
  popoverPaper: {
    boxShadow: "0 0px 3px 0 rgba(0, 0, 0, 0.2)",
    borderRadius: "6px",
    width: "200px",
    border: `1px solid ${colors.divider_line_white}`,
  },
  menuItem: {
    "&:hover": {
      backgroundColor: colors.baby_blue,
    },
  },
  menuItemTxt: {
    fontSize: "17px",
  },
  divider: {
    backgroundColor: colors.divider_line_white,
  },
}));

// Component
export default function SupersetCircuits() {
  const userType = JSON.parse(localStorage.getItem("AUTH_TOKEN"));
  const history = useHistory();
  const location = history.location;
  const query = querystring.parse(location.search);
  const view = query.view || views.exerciseList.key;
  const currentTab = location.pathname.split('/').pop();
  const queryParams = querystring.parse(location.search);
  const selectedProfile = useSelector(state => state?.clubConnect?.selectedProfile);

  const [openDropdown, setOpenDropdown] = useState(null);
  const classes = useStyles({ selected: openDropdown });
  const [superSetData, setSuperSetData] = useState({});
  const [circuitData, setCircuitsData] = useState({});
  const [selectedExerciseId, setSelectedExerciseId] = useState(queryParams?.exerciseId || null);
  const resetSelection = () => setSelectedExerciseId(!selectedExerciseId);

  useEffect(() => {
    resetSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const onGoBack = () => {
    return updateQuery(currentTab);
  };

  const viewExercisesList = () => {
    return updateQuery(location.search, { exerciseId: undefined, view: views.exerciseList.key });
  };
  const viewSuperSetsLink = (exerciseId) => {
    return updateQuery(location.search, { exerciseId: exerciseId, view: views.superSetDetails.key });
  };

   const viewExerciseDetailsLink = (exerciseId, showProgressionsRegressions = true) => {
    return updateQuery(location.search, {
      view: views.exerciseDetails.key,
      exerciseId: exerciseId,
      showProgressionsRegressions: showProgressionsRegressions,
    });
  };

   const viewSuperSetExDetailsLink = (exerciseId, showProgressionsRegressions = true) => {
    return updateQuery(location.search, { 
      exerciseId: exerciseId,
      view: views.superSetExerciseDetails.key,
      showProgressionsRegressions: showProgressionsRegressions,
    });
  };
    
  const viewCircuitLink = (exerciseId) => {
    return updateQuery(location.search, { exerciseId: exerciseId, view: views.circuitDetails.key });
  };

  const viewCircuitExDetailsLink = (exerciseId, showProgressionsRegressions = true) => {
    return updateQuery(location.search, {
      exerciseId: exerciseId,
      view: views.circuitExerciseDetails.key,
      showProgressionsRegressions: showProgressionsRegressions,
    });
  };

  const Sections = () => {
    return (
      <PageToolBar linkOptions={[
          {
            label: textStrings.superSets,
            to: SUPERSETS_LINK_TO,
          },
          {
            label: textStrings.circuits,
            to: CIRCUITS_LINK_TO,
          },
        ]}
        color={selectedProfile?.ClubId ? colors.graphite : colors.lightBlue}
      />
    );
  };

  const handleDropdown = (event) => {
    setOpenDropdown(event.currentTarget);
  };

  const handleSuperSetsMenu = () => {
    setSuperSetData({});
    handleDropdownClose();
    history.push({
      pathname: '/libraries/sets/supersets',
      search: viewSuperSetsLink(),
    });
  };

  const handleCircuitMenu = () => {
    setCircuitsData({});
    handleDropdownClose();
    history.push({
      pathname: '/libraries/sets/circuits',
      search: viewCircuitLink(),
    });
  };

  const handleDropdownClose = () => {
    setOpenDropdown(null);
  };

  const renderCreateMenu = () => {
    return (
      <Menu
        id="nav-menu"
        anchorEl={openDropdown}
        keepMounted
        open={Boolean(openDropdown)}
        onClose={handleDropdownClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom',horizontal: 'right' }} 
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          className: classes.menu,
        }}
        PopoverClasses={{
          paper: classes.popoverPaper,
        }}
      >
        <Typography className={classes.createNewTxt}>
          Create New:
          </Typography>
        <Divider className={classes.divider} height={4} />
        <MenuItem className={classes.menuItem} onClick={handleCircuitMenu}>
          <Typography className={classes.menuItemTxt}>{textStrings.circuit}</Typography>
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={handleSuperSetsMenu}>
          <Typography className={classes.menuItemTxt}>{textStrings.superSet}</Typography>
        </MenuItem>
      </Menu>
    );
  };

  return (
    <>
      <Route exact path="/libraries/sets">
        <Redirect to={SUPERSETS_LINK_TO} />
      </Route>
      <PageHeader title={textStrings.supersetsCircuits} />
      <FloatingCircleButton
        disabled={view === views.customExerciseDetails.key}
        onClick={(e) => {
          handleDropdown(e);
        }}
      />
      {renderCreateMenu()}
      <Sections />
      <Box>
        {userType?.cc_manager && (
          <Box style={{ padding: "20px 90px 0px 90px" }}>
            <NoticeBar />
          </Box>
        )}
        <CustomBreadcrumb items={breadcrumbItems} />
        <PanelContainer padding={"2px 90px 30px 125px"}> 
          <SlidingFlexTransition visible={!!views[view].exerciseList}>
            <Panel height={800}>
              <SupersetCircuitScreen
                selectedExerciseId={selectedExerciseId}
                setSelectedExerciseId={setSelectedExerciseId}
                resetSelection={resetSelection}
                viewSuperSetsLink={viewSuperSetsLink}
                setSuperSetData={setSuperSetData}
                viewCircuitLink={viewCircuitLink}
                setCircuitsData={setCircuitsData}
              />
            </Panel>
          </SlidingFlexTransition>
          <AddSuperSetsPanel
            visible={!!views[view].superSetDetails}
            resetSelection={resetSelection}
            backLink={onGoBack}
            superSetData={superSetData}
            setSuperSetData={setSuperSetData}
            viewSuperSetExDetailsLink={viewSuperSetExDetailsLink}
          />
          <AddCircuitsPanel
            visible={!!views[view].circuitDetails}
            resetSelection={resetSelection}
            backLink={onGoBack}
            circuitData={circuitData}
            setCircuitsData={setCircuitsData}
            viewCircuitExDetailsLink={viewCircuitExDetailsLink}
          />
          <ExerciseDetailsPanel
            visible={!!views[view].exerciseDetails}
            resetSelection={resetSelection}
            backLink={
              views[view].superSetDetails
                ? viewSuperSetsLink
                : views[view].circuitDetails
                ? viewCircuitLink
                : viewExercisesList
            }
            viewExerciseDetailsLink={
              views[view].superSetDetails
                ? viewSuperSetExDetailsLink
                : views[view].circuitDetails
                ? viewCircuitExDetailsLink
                : viewExerciseDetailsLink
            }
          />
        </PanelContainer>
      </Box>
    </>
  );
}
